/**
 * Grundbestand: allgemeine Zutaten und Haushaltswaren, die fast jeder
 * Haushalt kauft. Sie lassen sich in einem Schritt als Produkte anlegen;
 * danach findet die Einkaufsliste Warengruppe und weitere Namen, und
 * „Daten ergänzen“ holt Bild und Nährwerte aus den freien Datenbanken.
 *
 * Nährwerte werden bewusst nicht gespeichert: Ohne eigene Werte gelten die
 * Richtwerte aus nutrition.ts.
 *
 * Schreibweise je Zeile: Name; Einheit; weitere Namen (mit Komma getrennt).
 */
import type { ShoppingCategory } from './shopping.ts';

/** Ein Eintrag des Grundbestands. */
export interface BaseStockItem {
  readonly name: string;
  readonly category: ShoppingCategory;
  /** Übliche Einheit beim Einkaufen, leer = Stück. */
  readonly unit: string;
  readonly synonyms: readonly string[];
}

/** Rohdaten je Warengruppe. */
const RAW: Readonly<Record<ShoppingCategory, readonly string[]>> = {
  produce: [
    'Apfel;;Äpfel',
    'Birne;;',
    'Banane;;',
    'Orange;;Apfelsine',
    'Mandarine;;Clementine',
    'Zitrone;;',
    'Limette;;',
    'Grapefruit;;',
    'Kiwi;;',
    'Mango;;',
    'Ananas;;',
    'Avocado;;',
    'Weintrauben;g;Trauben',
    'Erdbeeren;g;',
    'Himbeeren;g;',
    'Heidelbeeren;g;Blaubeeren',
    'Brombeeren;g;',
    'Johannisbeeren;g;',
    'Kirschen;g;',
    'Pflaumen;g;Zwetschgen',
    'Aprikosen;g;Marillen',
    'Pfirsich;;',
    'Nektarine;;',
    'Wassermelone;;',
    'Honigmelone;;',
    'Granatapfel;;',
    'Rhabarber;g;',
    'Kartoffeln;kg;Kartoffel',
    'Süßkartoffel;;',
    'Zwiebel;;Speisezwiebel',
    'Rote Zwiebel;;',
    'Schalotte;;',
    'Knoblauch;;Knoblauchzehe',
    'Frühlingszwiebeln;Bund;Lauchzwiebeln',
    'Lauch;;Porree',
    'Möhren;g;Karotten,Möhre,Karotte',
    'Pastinake;;',
    'Petersilienwurzel;;',
    'Knollensellerie;;Sellerie',
    'Staudensellerie;;Stangensellerie',
    'Rote Bete;g;Rote Rüben',
    'Radieschen;Bund;',
    'Rettich;;',
    'Kohlrabi;;',
    'Brokkoli;;',
    'Blumenkohl;;',
    'Rosenkohl;g;',
    'Weißkohl;;',
    'Rotkohl;;Blaukraut,Rotkraut',
    'Wirsing;;',
    'Spitzkohl;;',
    'Grünkohl;g;',
    'Chinakohl;;',
    'Pak Choi;;',
    'Spinat;g;Blattspinat',
    'Mangold;g;',
    'Feldsalat;g;',
    'Rucola;g;Rauke',
    'Eisbergsalat;;',
    'Kopfsalat;;',
    'Romanasalat;;Römersalat',
    'Chicorée;;',
    'Gurke;;Salatgurke',
    'Zucchini;;',
    'Aubergine;;',
    'Paprika;;Paprikaschote',
    'Peperoni;;',
    'Chili;;Chilischote',
    'Tomaten;g;Tomate',
    'Kirschtomaten;g;Cherrytomaten,Cocktailtomaten',
    'Champignons;g;Pilze',
    'Kräuterseitlinge;g;',
    'Pfifferlinge;g;',
    'Austernpilze;g;',
    'Hokkaido-Kürbis;;Kürbis',
    'Butternut-Kürbis;;',
    'Fenchel;;',
    'Spargel weiß;g;Spargel',
    'Spargel grün;g;Grüner Spargel',
    'Grüne Bohnen;g;Brechbohnen,Prinzessbohnen',
    'Zuckerschoten;g;',
    'Maiskolben;;',
    'Ingwer;g;',
    'Petersilie;Bund;',
    'Schnittlauch;Bund;',
    'Basilikum;Topf;',
    'Dill;Bund;',
    'Koriander;Bund;',
    'Minze;Bund;Pfefferminze',
    'Thymian;Bund;',
    'Rosmarin;Bund;',
    'Salbei;Bund;',
    'Kresse;;',
  ],
  bakery: [
    'Brot;;Mischbrot',
    'Vollkornbrot;;',
    'Toastbrot;;Toast',
    'Brötchen;;Semmeln,Schrippen',
    'Baguette;;',
    'Laugenbrezel;;Brezel',
    'Croissant;;',
    'Fladenbrot;;',
    'Knäckebrot;;',
    'Tortilla-Wraps;;Wraps,Tortillas',
    'Zwieback;;',
  ],
  dairy: [
    'Milch;l;Vollmilch',
    'H-Milch;l;',
    'Buttermilch;ml;',
    'Schlagsahne;ml;Sahne',
    'Saure Sahne;g;',
    'Schmand;g;',
    'Crème fraîche;g;Creme fraiche',
    'Butter;g;',
    'Margarine;g;',
    'Joghurt;g;Naturjoghurt',
    'Griechischer Joghurt;g;',
    'Quark;g;Speisequark',
    'Magerquark;g;',
    'Frischkäse;g;',
    'Hüttenkäse;g;Körniger Frischkäse',
    'Mascarpone;g;',
    'Ricotta;g;',
    'Mozzarella;g;',
    'Feta;g;Schafskäse',
    'Parmesan;g;Parmigiano',
    'Gouda;g;',
    'Emmentaler;g;',
    'Bergkäse;g;',
    'Cheddar;g;',
    'Camembert;;',
    'Halloumi;g;Grillkäse',
    'Reibekäse;g;Geriebener Käse',
    'Eier;;Ei',
    'Blätterteig;;',
    'Pizzateig;;',
    'Filoteig;;Strudelteig',
  ],
  meat: [
    'Hähnchenbrust;g;Hähnchenbrustfilet,Hühnerbrust',
    'Hähnchenschenkel;g;Hähnchenkeulen',
    'Hähnchen;;Ganzes Hähnchen,Brathähnchen',
    'Putenbrust;g;Putenschnitzel',
    'Rinderhackfleisch;g;Rinderhack',
    'Hackfleisch gemischt;g;Hackfleisch,Gemischtes Hack',
    'Rindersteak;g;Steak',
    'Rindergulasch;g;Gulasch',
    'Schweinefilet;g;',
    'Schweinenacken;g;',
    'Schweineschnitzel;g;Schnitzel',
    'Kalbsschnitzel;g;',
    'Lammkeule;g;',
    'Speck;g;Bacon,Frühstücksspeck',
    'Schinkenwürfel;g;Speckwürfel',
    'Kochschinken;g;',
    'Salami;g;',
    'Bratwurst;;',
    'Wiener Würstchen;;Würstchen',
    'Lachsfilet;g;Lachs',
    'Räucherlachs;g;',
    'Kabeljau;g;Kabeljaufilet',
    'Seelachs;g;Seelachsfilet',
    'Forelle;;',
    'Garnelen;g;Shrimps,Krabben',
  ],
  frozen: [
    'TK-Erbsen;g;Erbsen',
    'TK-Spinat;g;',
    'TK-Beeren;g;Beerenmischung',
    'TK-Gemüsemischung;g;',
    'TK-Kräuter;g;',
    'Pommes frites;g;Pommes',
    'Vanilleeis;ml;Eis',
    'Fischstäbchen;;',
    'Pizza;;Tiefkühlpizza',
  ],
  baking: [
    'Weizenmehl Type 405;g;Mehl,Weizenmehl',
    'Weizenmehl Type 550;g;',
    'Dinkelmehl;g;',
    'Vollkornmehl;g;',
    'Roggenmehl;g;',
    'Speisestärke;g;Maisstärke',
    'Zucker;g;Kristallzucker',
    'Puderzucker;g;',
    'Brauner Zucker;g;Rohrzucker',
    'Vanillezucker;Päckchen;',
    'Backpulver;Päckchen;',
    'Natron;g;',
    'Trockenhefe;Päckchen;',
    'Frische Hefe;Würfel;Hefe',
    'Gelatine;Blatt;',
    'Kakaopulver;g;Backkakao',
    'Kuvertüre;g;',
    'Schokotropfen;g;',
    'Gemahlene Mandeln;g;Mandeln gemahlen',
    'Gemahlene Haselnüsse;g;Haselnüsse gemahlen',
    'Kokosraspel;g;',
    'Rosinen;g;',
    'Vanilleschote;;',
    'Paniermehl;g;Semmelbrösel',
    'Grieß;g;Hartweizengrieß',
    'Puddingpulver;Päckchen;',
    'Agar-Agar;g;',
  ],
  pantry: [
    'Spaghetti;g;',
    'Penne;g;',
    'Fusilli;g;',
    'Bandnudeln;g;Tagliatelle',
    'Lasagneplatten;g;',
    'Eiernudeln;g;',
    'Basmatireis;g;Reis',
    'Jasminreis;g;',
    'Risottoreis;g;Arborio',
    'Vollkornreis;g;',
    'Couscous;g;',
    'Bulgur;g;',
    'Quinoa;g;',
    'Haferflocken;g;',
    'Müsli;g;',
    'Cornflakes;g;',
    'Rote Linsen;g;',
    'Braune Linsen;g;Linsen',
    'Kichererbsen;Dose;',
    'Kidneybohnen;Dose;',
    'Weiße Bohnen;Dose;',
    'Mais;Dose;Zuckermais',
    'Passierte Tomaten;g;Passata',
    'Gehackte Tomaten;Dose;Stückige Tomaten,Dosentomaten',
    'Tomatenmark;g;',
    'Kokosmilch;ml;',
    'Thunfisch;Dose;',
    'Gemüsebrühe;g;Brühe',
    'Hühnerbrühe;g;',
    'Rinderfond;ml;Fond',
    'Polenta;g;Maisgrieß',
    'Gnocchi;g;',
    'Tortellini;g;',
    'Walnüsse;g;Walnusskerne',
    'Cashewkerne;g;',
    'Erdnüsse;g;',
    'Pinienkerne;g;',
    'Sonnenblumenkerne;g;',
    'Kürbiskerne;g;',
    'Leinsamen;g;',
    'Chiasamen;g;',
    'Mandeln;g;',
    'Haselnüsse;g;',
    'Oliven;g;',
    'Kapern;g;',
    'Getrocknete Tomaten;g;',
    'Essiggurken;Glas;Gewürzgurken',
    'Erdnussbutter;g;',
  ],
  spices: [
    'Salz;g;Speisesalz',
    'Meersalz;g;',
    'Pfeffer;g;Schwarzer Pfeffer',
    'Paprikapulver edelsüß;g;Paprikapulver',
    'Paprikapulver rosenscharf;g;',
    'Geräuchertes Paprikapulver;g;',
    'Chiliflocken;g;',
    'Cayennepfeffer;g;',
    'Currypulver;g;Curry',
    'Kreuzkümmel;g;Cumin',
    'Kümmel;g;',
    'Kurkuma;g;',
    'Zimt;g;',
    'Muskatnuss;;Muskat',
    'Oregano;g;',
    'Getrockneter Thymian;g;',
    'Getrockneter Rosmarin;g;',
    'Majoran;g;',
    'Lorbeerblätter;;Lorbeer',
    'Nelken;g;Gewürznelken',
    'Sternanis;g;',
    'Kardamom;g;',
    'Ingwerpulver;g;',
    'Knoblauchpulver;g;Knoblauchgranulat',
    'Italienische Kräuter;g;',
    'Kräuter der Provence;g;',
    'Olivenöl;ml;',
    'Rapsöl;ml;',
    'Sonnenblumenöl;ml;',
    'Sesamöl;ml;',
    'Kokosöl;g;',
    'Balsamico;ml;Aceto Balsamico',
    'Weißweinessig;ml;',
    'Apfelessig;ml;',
    'Senf;g;Mittelscharfer Senf',
    'Dijon-Senf;g;',
    'Ketchup;ml;Tomatenketchup',
    'Mayonnaise;ml;',
    'Sojasauce;ml;Sojasoße',
    'Worcestersauce;ml;',
    'Sambal Oelek;g;',
    'Rote Currypaste;g;Currypaste',
    'Fischsauce;ml;',
    'Ahornsirup;ml;',
    'Sesam;g;',
  ],
  sweets: [
    'Vollmilchschokolade;g;Schokolade',
    'Zartbitterschokolade;g;',
    'Gummibärchen;g;',
    'Kekse;g;',
    'Chips;g;Kartoffelchips',
    'Salzstangen;g;',
    'Honig;g;',
    'Marmelade;g;Konfitüre',
    'Nuss-Nougat-Creme;g;',
    'Müsliriegel;;',
  ],
  drinks: [
    'Mineralwasser;l;Wasser',
    'Apfelsaft;l;',
    'Orangensaft;l;',
    'Kaffeebohnen;g;Kaffee',
    'Gemahlener Kaffee;g;',
    'Schwarzer Tee;;Tee',
    'Kräutertee;;',
    'Grüner Tee;;',
    'Hafermilch;l;Haferdrink',
    'Sojamilch;l;Sojadrink',
    'Bier;;',
    'Weißwein;ml;',
    'Rotwein;ml;',
    'Sekt;;',
    'Cola;l;',
  ],
  household: [
    'Spülmittel;;',
    'Spülmaschinentabs;;Tabs',
    'Waschmittel;;',
    'Küchenrolle;;Küchenpapier',
    'Toilettenpapier;;Klopapier',
    'Frischhaltefolie;;',
    'Alufolie;;',
    'Backpapier;;',
    'Gefrierbeutel;;',
    'Müllbeutel;;',
    'Schwämme;;Spülschwamm',
    'Taschentücher;;',
    'Zahnpasta;;Zahncreme',
    'Duschgel;;',
    'Shampoo;;',
    'Seife;;Handseife',
    'Allzweckreiniger;;',
  ],
  other: [],
};

/** Der Grundbestand als Liste. */
export const BASE_STOCK: readonly BaseStockItem[] = Object.entries(RAW).flatMap(([category, lines]) =>
  lines.map((line): BaseStockItem => {
    const [name = '', unit = '', synonyms = ''] = line.split(';');
    return {
      name,
      category: category as ShoppingCategory,
      unit,
      synonyms: synonyms === '' ? [] : synonyms.split(','),
    };
  }),
);
